import {
  Typography,
  Grid,
  Box,
  Card,
  CardContent,
  Button,
  useMediaQuery,
  Modal,
} from "@mui/material";
import { useEffect, useState } from "react";
import MultiSelectWithAll from "../../components/selections/multiSelectWithAll";
import { useDispatch, useSelector } from "react-redux";
import { getCategoriesThunk } from "../../features/files/filesThunk";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import CloseIcon from "@mui/icons-material/Close";
import DownloadIcon from "@mui/icons-material/Download";
import DownloadFile from "../../components/downloadMediaFiles/downloadMediaFile";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  width: { xs: "95%", sm: "90%", md: "80%", lg: "70%" },
  maxWidth: 1200,
  maxHeight: { xs: "85vh", md: "80vh" },
  bgcolor: "background.paper",
  borderRadius: 2,
  boxShadow: 24,
  p: 3,
  overflow: "auto",
};

export default function FileTab() {
  const dispatch = useDispatch();
  const isMobile = useMediaQuery("(max-width:600px)");

  // 🌈 Multiple gradient colors (cycled per card)
  const colors = [
    "linear-gradient(135deg, #667eea 0%, #764ba2 100%)", // blue-purple
    "linear-gradient(135deg, #ff758c 0%, #ff7eb3 100%)", // pink-red
    "linear-gradient(135deg, #43cea2 0%, #185a9d 100%)", // green-teal
    "linear-gradient(135deg, #f7971e 0%, #ffd200 100%)", // orange-yellow
  ];

  const [fileCategories, setFileCategories] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [open, setOpen] = useState(false);

  // Redux data
  const { getCategories = [] } = useSelector(
    (state) => state.fileCategories || {}
  );
  const { getUserProfile = {} } = useSelector(
    (state) => state.contacts || {}
  );

  // 1️⃣ Load categories on mount if not already fetched
  useEffect(() => {
    if (!Array.isArray(getCategories) || getCategories.length === 0) {
      dispatch(getCategoriesThunk());
    }
  }, [dispatch, getCategories]);

  // 2️⃣ Files from user profile
  const files = Array.isArray(getUserProfile?.filesdata)
    ? getUserProfile.filesdata
    : [];

  // 3️⃣ Extract category IDs used in files
  const fileCategoryIds = Array.from(
    new Set(
      files
        .map(
          (f) =>
            f.file_categroy_id || // handle typo
            f.file_category_id ||
            f.category_id
        )
        .filter(Boolean)
        .map((id) => String(id))
    )
  );

  // 4️⃣ Filter available categories to those used in files
  const availableCategories = Array.isArray(getCategories)
    ? getCategories.filter((cat) =>
        fileCategoryIds.includes(String(cat.id || cat.category_id))
      )
    : [];

  // 5️⃣ Build dropdown options
  const categoryOptions = availableCategories.map((cat) => ({
    label:
      cat.category_name ||
      cat.name ||
      cat.label ||
      String(cat.category_id ?? cat.id),
    value: String(cat.category_id ?? cat.id),
  }));

  // 6️⃣ Default-select all available categories when they change
  useEffect(() => {
    if (categoryOptions.length > 0) {
      setFileCategories(categoryOptions);
    } else {
      setFileCategories([]);
    }
  }, [JSON.stringify(categoryOptions)]);

  // 7️⃣ Filter visible files by selected categories
  const visibleFiles = files.filter((file) => {
    const catId =
      file.file_categroy_id || file.file_category_id || file.category_id;
    if (!catId) return false;
    return fileCategories.some((c) => c.value === String(catId));
  });

  // 🖼️ Modal handlers
  const handleOpen = (file) => {
    setOpen(true);
    setSelectedFile(file);
  };

  const handleClose = () => setOpen(false);

  return (
    <>
      {/* --- Category Selector --- */}
      <Grid container spacing={1} alignItems="center">
        <Grid item size={{ xs: 12, md: 4 }}>
          <Box mt={2}>
            <MultiSelectWithAll
              name="fileCategories"
              label="Select Categories"
              options={categoryOptions}
              value={fileCategories}
              onChange={(name, selectedObjs) => setFileCategories(selectedObjs)}
            />
          </Box>
        </Grid>
      </Grid>

      {/* --- File Cards --- */}
      <Grid
        container
        spacing={2}
        mt={3}
        alignItems="center"
        sx={{
          maxHeight: files.length > 4 ? (isMobile ? 500 : 190) : "auto",
          overflowY: files.length > 2 ? "auto" : "visible",
        }}
      >
        {visibleFiles.map((file, index) => (
          <Grid item xs={12} md={4} key={index}>
            <Card
              onClick={() => handleOpen(file)}
              sx={{
                cursor: "pointer",
                borderRadius: 3,
                width: 220,
                height: 80,
                background: colors[index % colors.length],
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                position: "relative",
                boxShadow: "0 4px 12px rgba(0,0,0,0.15)",
                transition: "transform 0.2s ease, box-shadow 0.2s ease",
                "&:hover": {
                  transform: "translateY(-4px)",
                  boxShadow: "0 8px 16px rgba(0,0,0,0.25)",
                },
              }}
            >
              <Box
                mt={3}
                ml={1}
                sx={{ height: "100%", position: "relative", width: "100%" }}
              >
                {file.file_type === "image/png" ? (
                  <ImageIcon sx={{ fontSize: "50px", color: "white" }} />
                ) : (
                  <DescriptionIcon sx={{ fontSize: "50px", color: "white" }} />
                )}

                <CardContent
                  sx={{
                    textAlign: "center",
                    alignItems: "center",
                    justifyContent: "center",
                  }}
                >
                  <Typography
                    sx={{
                      position: "absolute",
                      top: 10,
                      left: 14,
                      right: 0,
                      opacity: 1.0,
                      fontWeight: 600,
                      fontSize: "0.75rem",
                      color: "#f0f0f0",
                    }}
                  >
                    {file.file_category_name || file.file_name}#{index + 1}
                  </Typography>
                </CardContent>
              </Box>
            </Card>
          </Grid>
        ))}

        {/* --- File Preview Modal --- */}
        <Modal
          open={open}
          aria-labelledby="modal-modal-title"
          aria-describedby="modal-modal-description"
        >
          <Box sx={style}>
            <Box
              sx={{
                width: "100%",
                height: { xs: "60vh", md: "60vh" },
                position: "relative",
              }}
            >
              {selectedFile?.file_url && (
                <>
                  {/* 🖼️ Image Preview */}
                  {selectedFile.file_type.startsWith("image/") && (
                    <img
                      src={selectedFile.file_url}
                      alt={selectedFile.file_name}
                      style={{ width: "100%", height: "100%" }}
                    />
                  )}

                  {/* 📄 PDF Preview */}
                  {selectedFile.file_type === "application/pdf" && (
                    <iframe
                      src={selectedFile.file_url}
                      title={selectedFile.file_name}
                      style={{ width: "100%", height: "100%", border: "none" }}
                    />
                  )}

                  {/* 📊 Excel Preview */}
                  {(selectedFile.file_type ===
                    "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" ||
                    selectedFile.file_type === "application/vnd.ms-excel") && (
                    <iframe
                      src={`https://view.officeapps.live.com/op/embed.aspx?src=${encodeURIComponent(
                        selectedFile.file_url
                      )}`}
                      title={selectedFile.file_name}
                      style={{ width: "100%", height: "100%", border: "none" }}
                    />
                  )}
                </>
              )}
            </Box>

            {/* Close + Download */}
            <CloseIcon
              onClick={handleClose}
              sx={{ position: "absolute", top: 8, right: 8, cursor: "pointer" }}
            />

            <Box p={3} sx={{ display: "flex", justifyContent: "flex-end" }}>
              <Button
                variant="contained"
                startIcon={<DownloadIcon />}
                onClick={() =>
                  DownloadFile(selectedFile.file_url, selectedFile.file_name)
                }
                sx={{
                  textTransform: "none",
                  "&:hover": { bgcolor: "var(--secondary)" },
                  position: "absolute",
                  right: 40,
                  cursor: "pointer",
                  background: "rgb(0, 118, 95)",
                  color: "white",
                }}
                aria-label="download file"
              >
                Download
              </Button>
            </Box>
          </Box>
        </Modal>
      </Grid>
    </>
  );
}
